import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';

import type { BlemishUploadTask } from '../../api/useBlemishImageUpload';

import CheckCircleFilled16 from '@common/components/icons/CheckCircleFilled16';
import ChevronDown12 from '@common/components/icons/ChevronDown12';
import ChevronUp12 from '@common/components/icons/ChevronUp12';
import { Close12 } from '@common/components/icons/Close12';

import { UploadStatusList } from './UploadStatusList';

import './UploadStatusPanel.scss';

export interface UploadStatusPanelProps {
	/** All upload tasks from the upload hook */
	tasks: BlemishUploadTask[];
	/** Whether the parent modal is open — resets panel state on each fresh open */
	isOpen: boolean;
}

export const UploadStatusPanel = ({
	tasks,
	isOpen,
}: UploadStatusPanelProps): React.JSX.Element | null => {
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [isDismissed, setIsDismissed] = useState(false);

	const prevIsOpenRef = useRef(false);
	const prevUploadingCountRef = useRef(0);
	const resetPanelOnNextBatchRef = useRef(false);
	const hiddenTaskIdsRef = useRef(new Set<string>());

	// Reset panel state when the modal opens
	useEffect(() => {
		if (isOpen && !prevIsOpenRef.current) {
			hiddenTaskIdsRef.current = new Set();
			setIsCollapsed(false);
			setIsDismissed(false);
		}
		prevIsOpenRef.current = isOpen;
	}, [isOpen]);

	// Filter out tasks from previous batches (hidden after X dismiss or all-succeed)
	const visibleTasks = tasks.filter((t) => !hiddenTaskIdsRef.current.has(t.id));
	const uploadingCount = visibleTasks.filter((t) => t.status === 'uploading').length;
	const hasUploadErrors = visibleTasks.some((t) => t.status === 'error');
	const allSucceeded = visibleTasks.length > 0 && uploadingCount === 0 && !hasUploadErrors;
	const showDismissButton = uploadingCount === 0;

	// Manage panel visibility as uploads start and finish
	useEffect(() => {
		const wasUploading = prevUploadingCountRef.current > 0;
		if (!wasUploading && uploadingCount > 0) {
			if (resetPanelOnNextBatchRef.current) {
				// X was pressed or all succeeded — hide old tasks and start fresh
				hiddenTaskIdsRef.current = new Set([
					...hiddenTaskIdsRef.current,
					...visibleTasks.filter((t) => t.status !== 'uploading').map((t) => t.id),
				]);
				setIsDismissed(false);
				setIsCollapsed(false);
				resetPanelOnNextBatchRef.current = false;
			} else {
				// Prior errors — keep existing panel, just make sure it's expanded
				setIsCollapsed(false);
			}
		} else if (wasUploading && uploadingCount === 0) {
			if (!hasUploadErrors) {
				// All succeeded — auto-collapse and start fresh on next batch
				setIsCollapsed(true);
				resetPanelOnNextBatchRef.current = true;
			}
		}
		prevUploadingCountRef.current = uploadingCount;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- visibleTasks omitted: .filter() creates a new array ref every render; uploadingCount and hasUploadErrors are the meaningful derived values
	}, [uploadingCount, hasUploadErrors]);

	if (visibleTasks.length === 0 || isDismissed) return null;

	return (
		<div className='upload-status-panel'>
			<div className='upload-status-panel__header'>
				{/* eslint-disable-next-line react/forbid-elements -- Rev Button pushes button-content off-screen via transform; native button required for layout control */}
				<button
					type='button'
					className='upload-status-panel__toggle'
					onClick={() => setIsCollapsed((prev) => !prev)}
					disabled={allSucceeded}
					aria-label={isCollapsed ? 'Expand upload panel' : 'Collapse upload panel'}>
					<span className='upload-status-panel__title-group'>
						<span className='upload-status-panel__title'>
							{uploadingCount > 0
								? `Uploading ${uploadingCount} remaining`
								: 'Uploads'}
						</span>
						{allSucceeded && (
							<span className='upload-status-panel__success-mark'>
								<CheckCircleFilled16 />
							</span>
						)}
					</span>
					{!allSucceeded && (
						<span className='upload-status-panel__chevron'>
							{isCollapsed ? <ChevronUp12 /> : <ChevronDown12 />}
						</span>
					)}
				</button>
				{showDismissButton && (
					// eslint-disable-next-line react/forbid-elements -- same constraint as toggle above
					<button
						type='button'
						className='upload-status-panel__dismiss'
						onClick={() => {
							setIsDismissed(true);
							resetPanelOnNextBatchRef.current = true;
						}}
						aria-label='Dismiss upload panel'>
						<Close12 />
					</button>
				)}
			</div>
			<AnimatePresence initial={false}>
				{!isCollapsed && (
					<motion.div
						key='upload-panel-body'
						className='upload-status-panel__body-anim'
						initial={{ height: 0 }}
						animate={{ height: 'auto' }}
						exit={{ height: 0 }}
						transition={{ ease: [0.4, 0, 0.2, 1], duration: 0.18 }}>
						<div className='upload-status-panel__body'>
							<UploadStatusList tasks={visibleTasks} />
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};
